// This function enhances a security contributor to have sleeve info contribution overrides
import { ProposablePortfolio_TEMP, ProposablePortfolioGroup_TEMP, Proposal_TEMP } from '@blk/aw-api-clients';
import { SleeveContributionProps, SleeveInfo } from '@blk/aw-bff-typescript-models';
import { get } from 'lodash';
import { getPortfolioOrPortfolioGroupFromProposal } from './ProposalUtils';

const isPortfolioGroup = (
  args: ProposablePortfolio_TEMP | ProposablePortfolioGroup_TEMP
): args is ProposablePortfolioGroup_TEMP => args.hasOwnProperty('portfolios');

export const enhanceContributorWithSleeveContribution = <T extends { positionID: string }>(
  contributors: T[],
  sleeveContribution: SleeveContributionProps,
  proposal: Proposal_TEMP,
  portfolioId: string,
  sleeveSelector: (
    tag: string,
    value: string,
    portfolio: ProposablePortfolio_TEMP | undefined
  ) => SleeveInfo | undefined
): (T & { sleeveOverrideLabel?: string; sleeveValue?: string })[] => {
  if (sleeveContribution.enableSleeveToggle && sleeveContribution.defaultSleeveToggle) {
    const sleeveMap: { [key: string]: string } = {};
    const portfolioOrPortfolioGroup = getPortfolioOrPortfolioGroupFromProposal(proposal, portfolioId);
    /*
     * Refactored in 2909764 to throw inside of calling component instead of getPortfolioOrPortfolioGroupFromProposal.
     * This component should be refactored further to handle undefined portfolio gracefully.
     */
    if (!portfolioOrPortfolioGroup) {
      throw new Error('Portfolio not found in SleeveUtils');
    }
    if (isPortfolioGroup(portfolioOrPortfolioGroup)) {
      portfolioOrPortfolioGroup.portfolios.forEach((portfolio) => {
        portfolio.positions
          .filter((pos) => pos.posInfo[sleeveContribution.sleeveTag])
          .forEach((pos) => {
            sleeveMap[`${portfolio.id}#${pos.id}`] = pos.posInfo[sleeveContribution.sleeveTag];
          });
      });
    } else {
      portfolioOrPortfolioGroup.positions
        .filter((pos) => pos.posInfo[sleeveContribution.sleeveTag])
        .forEach((pos) => (sleeveMap[pos.id] = pos.posInfo[sleeveContribution.sleeveTag]));
    }
    return contributors.map((contributor) => {
      const sleeveValue = sleeveMap[contributor.positionID];
      if (sleeveValue) {
        const portfolio = proposal.portfolioGroup.portfolios.find((portfolio) => portfolio.id === portfolioId);
        const sleeveInfo = sleeveSelector(sleeveContribution.sleeveTag, sleeveValue, portfolio);
        if (sleeveInfo) {
          return {
            ...contributor,
            sleeveValue: get(sleeveInfo.metadata, sleeveContribution.sleeveIdentifierField ?? '', sleeveValue),
            sleeveOverrideLabel: get(sleeveInfo.metadata, sleeveContribution.sleeveLabel ?? '', sleeveValue),
          };
        }

        return {
          ...contributor,
          sleeveOverrideLabel: sleeveValue,
        };
      }
      return contributor;
    });
  }
  return contributors;
};

export const createCombineSleeveSelector =
  (sleeveContribution: SleeveContributionProps | undefined, isPortGroup: boolean) =>
  (c: { sleeveOverrideLabel?: string; positionID: string; cusip: string }) => {
    if (sleeveContribution?.enableSleeveToggle && sleeveContribution.defaultSleeveToggle) {
      if (c.sleeveOverrideLabel) {
        if (isPortGroup) {
          const [portId] = c.positionID.split('#');
          return portId + c.sleeveOverrideLabel;
        }
        return c.sleeveOverrideLabel;
        // When the sleeve is enabled by defining the sleeve tag, but a certain contribution contains an empty sleeve label, we still want to display each position separately
      } else if (sleeveContribution.shouldGroupByPosition) {
        return c.positionID;
      }
    }
    return c.cusip;
  };
